import { StackScreenProps } from "@react-navigation/stack";
import { EditInvoiceArrivalRootParamList } from "../../../../types";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import { InvoiceArrivalDetailsFormProvider } from "../../components/InvoiceArrivalDetailsForm/InvoiceArrivalDetailsFormProvider";
import {
  Page,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  InvoiceArrivalDateFormInput,
  InvoiceDateFormInput,
  InvoiceDueDateFormInput,
  InvoiceIncGstFormInput,
  InvoiceNumberFormInput,
  OrderNumberFormInput,
} from "../../components/InvoiceArrivalDetailsForm/components";
import { InvoiceArrivalDetailsFormValues } from "../../components/InvoiceArrivalDetailsForm/types";
import { InvoiceArrival } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useFormContext } from "react-hook-form";
import { MUTATION_UPDATE_INVOICE_ARRIVAL } from "../../utils";
import { useMutation } from "@apollo/client";
import { useTranslation } from "../../../../utilities/i18n";
import { InvoiceFreightFormInput } from "../../components/InvoiceArrivalDetailsForm/components/InvoiceFreightFormInput";

export type GenericEditFormInputScreenProps = StackScreenProps<
  EditInvoiceArrivalRootParamList,
  "GenericEditFormInputScreen"
>;

const GenericEditFormInput = ({
  route,
  navigation,
}: GenericEditFormInputScreenProps) => {
  const { t } = useTranslation("invoice_arrival");
  const { setInvoiceArrival } = useInvoiceArrivalContextProvider();
  const { render } = route.params;

  const { formState, getValues, handleSubmit } =
    useFormContext<InvoiceArrivalDetailsFormValues>();

  const { isDirty, isValid, errors } = formState;

  const [m] = useMutation<{ updateInvoiceArrival: InvoiceArrival }>(
    MUTATION_UPDATE_INVOICE_ARRIVAL
  );

  const flashMessagesContext = useFlashMessages();

  const handleOnSubmit = async ({
    invoiceArrivalDate,
    invoiceDueDate,
    freightCost,
    locationId,
    supplierId,
    ...input
  }: InvoiceArrivalDetailsFormValues) => {
    try {
      const result = await m({
        variables: {
          input: {
            ...input,
            dueDate: invoiceDueDate,
            arrivalDate: invoiceArrivalDate,
            freightCost: freightCost ? parseFloat(freightCost) : undefined,
          },
        },
      });

      if (result.data?.updateInvoiceArrival) {
        setInvoiceArrival(result.data?.updateInvoiceArrival);
        navigation.goBack();
      }
    } catch (err: any) {
      flashMessagesContext.showMessage({
        message: err.message ?? "Something went wrong",
        severity: "error",
      });
    }
  };

  React.useEffect(() => {
    const disabled = !isDirty || !isValid;
    navigation.setOptions({
      headerTitleAlign: "center",
      headerTitle: `Edit ${t(`form.${render}.label`)}`,
      headerRight: () => {
        return (
          <Button
            label="Save"
            isAction
            disabled={disabled}
            onPress={handleSubmit(handleOnSubmit)}
            size="compact"
          ></Button>
        );
      },
    });
  }, [isValid, render, handleOnSubmit, isDirty]);

  return (
    <Page scrollable={false} cover>
      {renderToFormInput(render)}
    </Page>
  );
};

export const GenericEditFormInputScreen = (
  props: GenericEditFormInputScreenProps
) => {
  const { invoiceArrival } = useInvoiceArrivalContextProvider();

  return (
    <InvoiceArrivalDetailsFormProvider
      defaultFormValues={toFormInput(invoiceArrival)}
    >
      <GenericEditFormInput {...props} />
    </InvoiceArrivalDetailsFormProvider>
  );
};

const toFormInput = (
  invoiceArrival: InvoiceArrival
): Partial<InvoiceArrivalDetailsFormValues> => {
  return {
    id: invoiceArrival.id,
    invoiceNumber: invoiceArrival.invoiceNumber ?? "",
    orderNumber: invoiceArrival.orderNumber ?? "",
    invoiceDate: invoiceArrival.invoiceDate
      ? new Date(invoiceArrival.invoiceDate)
      : undefined,
    invoiceArrivalDate: invoiceArrival.arrivalDate
      ? new Date(invoiceArrival.arrivalDate)
      : undefined,
    invoiceDueDate: invoiceArrival.dueDate
      ? new Date(invoiceArrival.dueDate)
      : undefined,
    freightCost: (invoiceArrival.freightCost ?? 0).toString(),
    costPriceIncGST: invoiceArrival.costPriceIncGST,
    supplierId: invoiceArrival.supplierId,
    locationId: invoiceArrival.locationId,
  };
};

const renderToFormInput = (render: string) => {
  switch (render) {
    case "invoice_number":
      return <InvoiceNumberFormInput />;
    case "invoice_date":
      return <InvoiceDateFormInput />;
    case "arrival_date":
      return <InvoiceArrivalDateFormInput />;
    case "invoice_due_date":
      return <InvoiceDueDateFormInput />;
    case "invoice_freight":
      return <InvoiceFreightFormInput />;
    case "invoice_cost_price_inc_gst":
      return <InvoiceIncGstFormInput />;
    case "order_number":
      return <OrderNumberFormInput />;
    default:
      return null;
  }
};
